import type { DataApiInputParams } from 'src/types/api';

import { CORE_MS_BASE_URL } from './customBaseQuery';
import type { Builder } from './utils';
import { appendFormData, getHeaders, getQueryString } from './utils';

/**
 * Page window applied when a caller asks for workflows without paging itself.
 * It is a ceiling, not a total: a caller that needs every row must page with
 * its own `p`/`ps`, because rows past this window are not reported as missing.
 */
const WORKFLOW_LIST_DEFAULT_PAGE = 1;
const WORKFLOW_LIST_DEFAULT_PAGE_SIZE = 100;

export default (builder: Builder) => {
  return {
    getWorkflow: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const base =
          arg?.params !== undefined &&
          arg.params !== null &&
          typeof arg.params === 'object'
            ? { ...(arg.params as Record<string, unknown>) }
            : {};
        // Defaults stay for callers that do not paginate; a caller that passes
        // `p`/`ps` in params overrides them.
        const queryParams = {
          p: WORKFLOW_LIST_DEFAULT_PAGE,
          ps: WORKFLOW_LIST_DEFAULT_PAGE_SIZE,
          ...base
        };
        const url = `${CORE_MS_BASE_URL}workflows?${getQueryString(
          queryParams
        )}`;
        return fetchWithBQ({
          url,
          method: 'GET',
          ...getHeaders(url),
          ms: true
        });
      },
      providesTags: ['Workflows']
    }),
    getWorkflowRuns: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const base =
          arg?.params !== undefined &&
          arg.params !== null &&
          typeof arg.params === 'object'
            ? { ...(arg.params as Record<string, unknown>) }
            : {};
        const queryParams = { ...base, p: 1, ps: 100 };
        const url = `${CORE_MS_BASE_URL}run-workflow?${getQueryString(
          queryParams
        )}`;
        return fetchWithBQ({
          url,
          method: 'GET',
          ...getHeaders(url),
          ms: true
        });
      },
      providesTags: ['Workflows']
    }),
    getWorkflowRunDetail: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const base =
          arg?.params !== undefined &&
          arg.params !== null &&
          typeof arg.params === 'object'
            ? { ...(arg.params as Record<string, unknown>) }
            : {};
        const url = `${CORE_MS_BASE_URL}run-workflow/detail?${getQueryString(
          base
        )}`;
        return fetchWithBQ({
          url,
          method: 'GET',
          ...getHeaders(url),
          ms: true
        });
      },
      providesTags: ['Workflows']
    }),
    getWorkflowTrigger: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const base =
          arg?.params !== undefined &&
          arg.params !== null &&
          typeof arg.params === 'object'
            ? { ...(arg.params as Record<string, unknown>) }
            : {};
        const url = `${CORE_MS_BASE_URL}workflows/trigger?${getQueryString(
          base
        )}`;
        return fetchWithBQ({
          url,
          method: 'GET',
          ...getHeaders(url),
          ms: true
        });
      },
      providesTags: ['Workflows']
    }),
    getWorkflowUsage: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const base =
          arg?.params !== undefined &&
          arg.params !== null &&
          typeof arg.params === 'object'
            ? { ...(arg.params as Record<string, unknown>) }
            : {};
        const query = getQueryString(base);
        const url = `${CORE_MS_BASE_URL}workflows/usage${
          query ? `?${query}` : ''
        }`;
        return fetchWithBQ({
          url,
          method: 'GET',
          ...getHeaders(url),
          ms: true
        });
      },
      providesTags: ['Workflows']
    }),
    getWorkflowsPricingStats: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const params = (arg?.params ?? {}) as Record<string, unknown>;
        const idsRaw = params.workflow_ids ?? params.ids;
        const workflow_ids = (
          Array.isArray(idsRaw) ? (idsRaw as unknown[]) : [idsRaw]
        )
          .map((v) => `${v ?? ''}`.trim())
          .filter((v) => v !== '');
        const url = `${CORE_MS_BASE_URL}workflows/stats?${getQueryString({
          workflow_ids
        })}`;
        return fetchWithBQ({
          url,
          method: 'GET',
          ...getHeaders(url),
          ms: true
        });
      }
    }),
    getWorkflowVersions: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const base =
          arg?.params !== undefined &&
          arg.params !== null &&
          typeof arg.params === 'object'
            ? { ...(arg.params as Record<string, unknown>) }
            : {};
        const queryParams = { ...base, p: 1, ps: 100 };
        const url = `${CORE_MS_BASE_URL}workflows/versions?${getQueryString(
          queryParams
        )}`;
        return fetchWithBQ({
          url,
          method: 'GET',
          ...getHeaders(url),
          ms: true
        });
      },
      providesTags: ['Workflows']
    }),
    runWorkflow: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const params = arg?.params as
          | {
              workflow_id?: unknown;
              input?: unknown;
            }
          | undefined;

        const widRaw = params?.workflow_id;
        const workflowId =
          widRaw !== undefined && widRaw !== null && `${widRaw}`.trim() !== ''
            ? `${widRaw}`.trim()
            : '';

        const qs =
          workflowId !== ''
            ? `?${getQueryString({ workflow_id: workflowId })}`
            : '';
        const url = `${CORE_MS_BASE_URL}run-workflow${qs}`;
        const runPayload =
          params?.input && typeof params.input === 'object'
            ? (params.input as Record<string, unknown>)
            : {};
        const formPayload = { ...runPayload };
        if (
          Object.prototype.hasOwnProperty.call(formPayload, 'input') &&
          formPayload.input &&
          typeof formPayload.input === 'object'
        ) {
          formPayload.input = JSON.stringify(formPayload.input);
        }
        const formData = appendFormData(formPayload);

        return fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),
    createWorkflow: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CORE_MS_BASE_URL}workflows`;
        return fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: ['Workflows']
    }),
    updateWorkflow: builder.mutation({
      async queryFn({ params }: DataApiInputParams, _, __, fetchWithBQ) {
        const { workflow_id: wid, ...body } = (params ?? {}) as Record<
          string,
          unknown
        >;
        const id =
          wid !== undefined && wid !== null && `${wid}`.trim() !== ''
            ? `${wid}`.trim()
            : '';
        const url = `${CORE_MS_BASE_URL}workflows${
          id !== '' ? `?${getQueryString({ workflow_id: id })}` : ''
        }`;
        const formData = appendFormData(body);
        return fetchWithBQ({
          url,
          method: 'PUT',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: ['Workflows']
    }),
    patchWorkflow: builder.mutation({
      async queryFn({ params }: DataApiInputParams, _, __, fetchWithBQ) {
        const { workflow_id: wid, ...body } = (params ?? {}) as Record<
          string,
          unknown
        >;
        const id =
          wid !== undefined && wid !== null && `${wid}`.trim() !== ''
            ? `${wid}`.trim()
            : '';
        const url = `${CORE_MS_BASE_URL}workflows${
          id !== '' ? `?${getQueryString({ workflow_id: id })}` : ''
        }`;
        const formData = appendFormData(body);
        return fetchWithBQ({
          url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: ['Workflows']
    }),
    duplicateWorkflow: builder.mutation({
      async queryFn({ params }: DataApiInputParams, _, __, fetchWithBQ) {
        const { workflow_id } = (params ?? {}) as Record<string, unknown>;
        const url = `${CORE_MS_BASE_URL}workflows/duplicate`;
        const formData = appendFormData({ workflow_id });
        return fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: ['Workflows']
    }),
    deleteWorkflow: builder.mutation({
      async queryFn({ params }: DataApiInputParams, _, __, fetchWithBQ) {
        const { id } = (params ?? {}) as Record<string, unknown>;
        const url = `${CORE_MS_BASE_URL}workflows`;
        const formData = appendFormData({ id });
        return fetchWithBQ({
          url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: ['Workflows']
    }),
    getWorkflowWebhook: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const base =
          arg?.params !== undefined &&
          arg.params !== null &&
          typeof arg.params === 'object'
            ? { ...(arg.params as Record<string, unknown>) }
            : {};
        const url = `${CORE_MS_BASE_URL}workflow-webhooks?${getQueryString(
          base
        )}`;
        const result = await fetchWithBQ({
          url,
          method: 'GET',
          ...getHeaders(url),
          ms: true
        });
        return result;
      },
      providesTags: ['WorkflowWebhook']
    }),
    updateWorkflowWebhook: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const params = (arg?.params ?? {}) as Record<string, unknown>;
        const url = `${CORE_MS_BASE_URL}workflow-webhooks`;
        const formData = appendFormData(params);
        const result = await fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });

        return result;
      }
    }),
    connectOutlook: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const incoming = (arg?.params ??
          (arg as unknown as Record<string, unknown>) ??
          {}) as Record<string, unknown>;
        const params: Record<string, unknown> = {
          workflow_id:
            typeof incoming.workflow_id === 'string'
              ? incoming.workflow_id
              : '',
          provider:
            typeof incoming.provider === 'string' ? incoming.provider : '',
          trigger_type:
            typeof incoming.trigger_type === 'string'
              ? incoming.trigger_type
              : ''
        };
        // Triggers scope their subscription with extra identifiers (Teams
        // channel/chat, Outlook search query), so forward whatever the drawer
        // collected.
        for (const key of [
          'channel_id',
          'team_id',
          'chat_id',
          'filter_keyword',
          'search_query'
        ]) {
          const value = incoming[key];
          if (typeof value === 'string' && value.trim() !== '') {
            params[key] = value.trim();
          }
        }
        const url = `${CORE_MS_BASE_URL}subscription`;
        const formData = appendFormData(params);
        const result = await fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
        return result;
      }
    })
  };
};
